import { CalendarDotsIcon, ClockIcon, DownloadSimpleIcon } from '@phosphor-icons/react';
import { PageHeader } from 'components/PageHeader';
import { OrderLineStopType } from 'src/types/enums';
import React from 'react';
import useSWR from 'swr';
import { Link, useParams } from 'react-router-dom';
import { invariant, nullthrows } from '@utils/invariant';
import { fetchCustomerPortalInvoice } from './models';

import { Breadcrumb } from 'components/Breadcrumb';
import { DataField } from 'components/DataField';
import { formatDate, formatInputTime } from '../../../utils/date';
import { getInvoiceTotals } from '../../../domain/invoice-totals';
import { decimateAndFormatNumber } from '../../../utils/number';
import { useTranslation } from '../../../contexts/translation-context';
import { PageHeading } from 'components/PageHeading';
import { Card } from 'components/Card';
import { useDownloadDocument } from 'src/app/document/hooks/useDownloadDocument';
import { Button } from 'components/button/Button';
import { DelayedSpinner } from 'components/Spinner';

export const CustomerPortalInvoicePage = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  invariant(invoiceId);
  const { i18n } = useTranslation();
  const { data, isLoading } = useSWR(['customer-portal-invoice', invoiceId], () =>
    fetchCustomerPortalInvoice(invoiceId),
  );
  const { downloadFile } = useDownloadDocument();

  if (isLoading) {
    return <DelayedSpinner size={8} />;
  }

  const invoice = nullthrows(data, i18n('customerPortal.invoices.notFound'));
  const totals = getInvoiceTotals([
    ...invoice.orders
      .map((o) =>
        o.lines
          .map((l) =>
            l.sales.map((s) => {
              return {
                ...s,
                vatRateId: s.vatRate.id,
              };
            }),
          )
          .flat(),
      )
      .flat(),
    ...invoice.manualInvoiceLines.map((l) => {
      return {
        ...l,
        vatRateId: l.vatRate.id,
      };
    }),
  ]);

  const title = i18n('customerPortal.invoices.invoice', {
    invoiceNumber: invoice.invoiceNumber ?? i18n('draft'),
  });
  return (
    <>
      <PageHeader title={title} />

      <div>
        <PageHeading
          noMarginBottom
          leftSide={
            <Breadcrumb
              parentItem={{
                name: i18n('customerPortal.invoices.invoices'),
                to: '..',
              }}
              currentItem={title}
            />
          }
          rightSide={
            <Button
              color="primary"
              isDisabled={!invoice.document}
              iconLeft={<DownloadSimpleIcon className="button-icon" />}
              onTrigger={() => (invoice.document ? downloadFile(invoice.document) : null)}
            >
              {i18n('download.download')}
            </Button>
          }
        />

        <div className="grid gap-4 p-7">
          <Card title={i18n('customerPortal.invoices.generalInformation')}>
            <div className="grid grid-cols-4 gap-4">
              <DataField title={i18n('customerPortal.invoices.structuredComment')}>
                {invoice.structuredComment}
              </DataField>
              <DataField title={i18n('customerPortal.invoices.invoiceDate')}>{formatDate(invoice.date)}</DataField>
              {!!invoice.customerRef && (
                <DataField title={i18n('customerPortal.invoices.reference')}>{invoice.customerRef}</DataField>
              )}
              {invoice.sentAt && (
                <DataField title={i18n('customerPortal.invoices.sentAt')}>{formatDate(invoice.sentAt)}</DataField>
              )}
              {invoice.sentAt && (
                <DataField title={i18n('customerPortal.invoices.dueDate')}>{formatDate(invoice.expiresAt)}</DataField>
              )}
            </div>
          </Card>

          {invoice.orders.length > 0 && (
            <>
              <div className="heading-two">{i18n('customerPortal.orders.orders')}</div>

              {invoice.orders.map((order) => {
                const orderTotals = getInvoiceTotals(
                  order.lines
                    .map((l) =>
                      l.sales.map((s) => {
                        return {
                          ...s,
                          vatRateId: s.vatRate.id,
                        };
                      }),
                    )
                    .flat(),
                );

                return (
                  <div className="card flex flex-col gap-3.5 p-5" key={`order-${order.orderNumber}`}>
                    <div className="flex justify-between">
                      <Link to={`../../orders/${order.id}`} className="heading-four hover:text-brand-text">
                        {i18n('customerPortal.orders.orderLink', {
                          orderNumber: order.orderNumber ?? '',
                          customerRef: order.customerRef ?? '',
                        })}
                      </Link>
                    </div>

                    <div className="flex flex-col gap-3.5">
                      {order.lines.map((line) => {
                        return (
                          <div className="flex flex-col gap-3.5" key={`line-${line.id}`}>
                            <div className="flex flex-col gap-1.5">
                              {line.stops
                                .sort((a, b) => a.sequenceIndex - b.sequenceIndex)
                                .map((stop) => {
                                  return (
                                    <div className="grid grid-cols-4 gap-4 text-sm" key={`line-${line.id}-${stop.id}`}>
                                      <div className="font-medium">
                                        {stop.type === OrderLineStopType.Load
                                          ? i18n('customerPortal.invoices.load')
                                          : i18n('customerPortal.invoices.unload')}
                                      </div>

                                      <div className="text-fg-secondary">
                                        {stop.location.country}-{stop.location.name}
                                      </div>

                                      <div className="flex gap-2 items-center font-regular">
                                        <CalendarDotsIcon className="w-4 h-4" />
                                        {formatDate(stop.date)}
                                      </div>
                                      <div className="flex gap-2 items-center font-regular">
                                        <ClockIcon className="w-4 h-4" />
                                        {`${formatInputTime(stop.timeStart)}-${formatInputTime(stop.timeEnd)}`}
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>

                            <div className="border-y border-border py-3.5 text-sm">
                              <div className="grid grid-cols-5 gap-x-4 mb-1">
                                <div className="label-text">{i18n('customerPortal.invoices.description')}</div>
                                <div className="label-text">{i18n('customerPortal.invoices.amount')}</div>
                                <div className="label-text">{i18n('customerPortal.invoices.unitPrice')}</div>
                                <div className="label-text">{i18n('customerPortal.invoices.total')}</div>
                                <div className="label-text">{i18n('customerPortal.invoices.vat')}</div>
                              </div>

                              <div className="grid grid-cols-5 gap-x-4 gap-y-1">
                                {line.sales.map((sale) => {
                                  return (
                                    <React.Fragment key={`sale-${sale.id}`}>
                                      <div>{sale.productType.nameNl}</div>
                                      <div className="whitespace-nowrap">
                                        {decimateAndFormatNumber(sale.amount, 2, {
                                          decimalSeperator: ',',
                                        })}
                                      </div>
                                      <div className="whitespace-nowrap">
                                        €{' '}
                                        {decimateAndFormatNumber(sale.unitPrice, 2, {
                                          decimalSeperator: ',',
                                        })}
                                      </div>
                                      <div className="whitespace-nowrap">
                                        €{' '}
                                        {decimateAndFormatNumber((sale.amount * sale.unitPrice) / 100, 2, {
                                          decimalSeperator: ',',
                                        })}
                                      </div>
                                      <div className="whitespace-nowrap">
                                        €{' '}
                                        {decimateAndFormatNumber(
                                          (sale.amount * sale.unitPrice * sale.vatRatePercentage) / 1000000,
                                          2,
                                          {
                                            decimalSeperator: ',',
                                          },
                                        )}{' '}
                                        -{' '}
                                        {decimateAndFormatNumber(sale.vatRatePercentage, 2, {
                                          decimalSeperator: ',',
                                        })}
                                        %
                                      </div>

                                      {!!sale.externalNote && (
                                        <div className="col-span-5">
                                          <div className="text-sm">
                                            {i18n('customerPortal.invoices.extraInformation', {
                                              extraInformation: sale.externalNote,
                                            })}
                                          </div>
                                        </div>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      <div className="text-sm font-medium whitespace-nowrap">
                        {i18n('customerPortal.invoices.orderTotal', {
                          total: decimateAndFormatNumber(orderTotals.totalExclVat, 2, {
                            decimalSeperator: ',',
                          }),
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {invoice.manualInvoiceLines.length > 0 && (
            <div className="card p-5 text-sm">
              <div className="grid grid-cols-5 gap-x-4 mb-1">
                <div className="label-text">{i18n('customerPortal.invoices.description')}</div>
                <div className="label-text">{i18n('customerPortal.invoices.amount')}</div>
                <div className="label-text">{i18n('customerPortal.invoices.unitPrice')}</div>
                <div className="label-text">{i18n('customerPortal.invoices.total')}</div>
                <div className="label-text">{i18n('customerPortal.invoices.vat')}</div>
              </div>

              <div className="grid grid-cols-5 gap-x-4 gap-y-1">
                {invoice.manualInvoiceLines.map((line) => {
                  return (
                    <React.Fragment key={`line-${line.id}`}>
                      <div>{line.description}</div>
                      <div className="whitespace-nowrap">
                        {decimateAndFormatNumber(line.amount, 2, {
                          decimalSeperator: ',',
                        })}
                      </div>
                      <div className="whitespace-nowrap">
                        €{' '}
                        {decimateAndFormatNumber(line.unitPrice, 2, {
                          decimalSeperator: ',',
                        })}
                      </div>
                      <div className="whitespace-nowrap">
                        €{' '}
                        {decimateAndFormatNumber((line.amount * line.unitPrice) / 100, 2, {
                          decimalSeperator: ',',
                        })}
                      </div>
                      <div className="whitespace-nowrap">
                        €{' '}
                        {decimateAndFormatNumber((line.amount * line.unitPrice * line.vatRatePercentage) / 1000000, 2, {
                          decimalSeperator: ',',
                        })}{' '}
                        -{' '}
                        {decimateAndFormatNumber(line.vatRatePercentage, 2, {
                          decimalSeperator: ',',
                        })}
                        %
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <div className="grid w-[520px] grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
              <div className="label-text">{i18n('customerPortal.invoices.totalExclVat')}</div>
              <div className="whitespace-nowrap">
                €{' '}
                {decimateAndFormatNumber(totals.totalExclVat, 2, {
                  decimalSeperator: ',',
                })}
              </div>
              <div className="label-text">{i18n('customerPortal.invoices.totalVat')}</div>
              <div className="whitespace-nowrap">
                €{' '}
                {decimateAndFormatNumber(totals.totalVat, 2, {
                  decimalSeperator: ',',
                })}
              </div>
              <div className="label-text">{i18n('customerPortal.invoices.totalInclVat')}</div>
              <div className="whitespace-nowrap">
                €{' '}
                {decimateAndFormatNumber(totals.totalInclVat, 2, {
                  decimalSeperator: ',',
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
